import { UploadedFile } from '@komodo-design-system/komodo-core-web-components';
import { bulkUploadService } from 'api/bulkUploadServices';

export interface AlertState {
  message: string;
  open: boolean;
  props: { variant?: boolean; [key: string]: any };
}

export interface UploadProgress {
  show: boolean;
  completed: number;
  total: number;
  currentFile: string;
  stage: string;
}

export interface ValidationResult {
  isValid: boolean;
  validFiles: UploadedFile[];
  oversizedFile: string | null;
}

export class BulkUploadUtils {
  private static readonly MAX_FILE_SIZE = 19 * 1024 * 1024; // 19MB in bytes
  private static readonly MAX_FILE_COUNT = 2;

  /**
   * Validates file size
   */
  static validateFileSize(file: UploadedFile): boolean {
    return file.fileReference ? file.fileReference.size <= this.MAX_FILE_SIZE : false;
  }

  /**
   * Validates file count
   */
  static validateFileCount(files: UploadedFile[]): boolean {
    return files.length <= this.MAX_FILE_COUNT;
  }

  /**
   * Processes files and separates valid from oversized ones
   */
  static processValidFiles(filesToProcess: UploadedFile[]): ValidationResult {
    const validFiles: UploadedFile[] = [];
    let oversizedFile: string | null = null;

    filesToProcess.forEach(file => {
      if (this.validateFileSize(file)) {
        file.status = 'completed';
        validFiles.push(file);
      } else {
        oversizedFile = file.fileReference?.name || file.name || 'Unknown file';
      }
    });

    return {
      isValid: oversizedFile === null,
      validFiles,
      oversizedFile
    };
  }

  /**
   * Validates file types based on metadata-only mode
   */
  static validateFileTypes(filesList: UploadedFile[], isMetadataOnly: boolean): {
    isValid: boolean;
    errorMessage: string;
  } {
    const fileExtensions = filesList.map(file => {
      const fileName = file.fileReference?.name || '';
      return fileName.split('.').pop()?.toLowerCase() || '';
    });

    if (isMetadataOnly) {
      // Metadata only: single xlsx file
      if (fileExtensions.length !== 1 || fileExtensions[0] !== 'xlsx') {
        return {
          isValid: false,
          errorMessage: 'Please upload a proper file type: Excel (.xlsx) only.'
        };
      }
    } else {
      // Normal mode: exactly 2 files (xlsx + zip)
      if (fileExtensions.length !== 2 || 
          !fileExtensions.includes('xlsx') || 
          !fileExtensions.includes('zip')) {
        return {
          isValid: false,
          errorMessage: 'Please upload proper file types: Excel (.xlsx) and ZIP (.zip).'
        };
      }
    }

    return { isValid: true, errorMessage: '' };
  }

  /**
   * Filters existing files that match new upload
   */
  static filterExistingFiles(
    newFiles: UploadedFile[], 
    existingFiles: UploadedFile[]
  ): UploadedFile[] {
    return newFiles.filter(file => 
      existingFiles.some(f => f.fileReference?.name === file.fileReference?.name)
    );
  }

  /**
   * Creates alert state object
   */
  static createAlert(
    message: string, 
    isOpen: boolean = true, 
    variant: boolean = false
  ): AlertState {
    return {
      message,
      open: isOpen,
      props: variant ? { variant } : {}
    };
  }

  /**
   * Creates job ID via API call
   */
  static async createJobId(
    status: string,
    type: string,
    errorMessage: any,
    userName: string,
    jobName: string,
    bulkJobId: string,
    fileName: string,
  ): Promise<string | null> {
    try {
      const body = {
        assetCount: 0,
        status,
        errorMessage,
        userId: userName,
        jobName,
        fileName,
        errorFilePath: ''
      };

      const response = await bulkUploadService.bulkJobId(body, type, bulkJobId);
      const data = await response.json();

      if (response.status === 200 && data?.errorMessage === '') {
        return data.bulkUploadId;
      }
      
      return null;
    } catch (error) {
      console.error('Error creating job ID:', error);
      return null;
    }
  }

  /**
   * Creates progress update handler
   */
  static createProgressHandler(
    setUploadProgress: React.Dispatch<React.SetStateAction<UploadProgress>>
  ) {
    return (update: Partial<UploadProgress> | ((prev: UploadProgress) => UploadProgress)) => {
      if (typeof update === 'function') {
        setUploadProgress(prev => update(prev));
      } else {
        setUploadProgress(prev => ({ ...prev, ...update }));
      }
    };
  }

  /**
   * Calculates upload progress percentage
   */
  static calculateProgress(completed: number, total: number): number {
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  }

  /**
   * Validation error messages
   */
  static readonly ERROR_MESSAGES = {
    FILE_COUNT_EXCEEDED: 'You can upload a maximum of 2 files only.',
    FILE_SIZE_EXCEEDED: 'Maximum file size should be 20 MB per file.',
    JOB_ID_CREATION_FAILED: 'Unable to create bulk job id. Please try again.',
    UPLOAD_FAILED: 'Failed, please check job details page',
    FILE_REJECTED: (fileName: string) => `File "${fileName}" was rejected. Please check the file type and size.`,
    CLEAR_ALL: 'This will reset the values to default. Do you want to continue?'
  } as const;
}
